/**
 * LeftSidebar.js - OnTimeLog 내비게이션
 */

import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckSquare,
  Settings,
  BarChart2,
  FileText,
  Search,
  MessageCircle,
} from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import { useUserRole } from '../contexts/UserRoleContext';

const COLLAPSED_WIDTH = 60;
const DOOR_TRANSITION = 'cubic-bezier(0.26, 0.86, 0.44, 1)';
const DOOR_DURATION = 420;

// OnTimeLog 메인 메뉴
const PRIMARY_MENU = [
  { icon: BarChart2, path: '/dashboard', mobilePath: '/m/dashboard', label: '대시보드', role: ['user', 'manager', 'admin', 'root'] },
  { icon: Clock, path: '/job-time-input', mobilePath: '/m/log', label: '업무 투입시간', role: ['user', 'manager', 'admin', 'root'] },
  { icon: CheckSquare, path: '/todo-list', mobilePath: '/m/todo', label: 'To-Do 리스트', role: ['user', 'manager', 'admin', 'root'] },
  { icon: FileText, path: '/work-details-report', mobilePath: '/m/work-details-report', label: '업무내역 리포트', role: ['user', 'manager', 'admin', 'root'] },
  { icon: Search, path: '/experience-search', mobilePath: '/m/experience-search', label: '업무 경험 찾기', role: ['user', 'manager', 'admin', 'root'] },
  { icon: Settings, path: '/admin', mobilePath: '/m/admin', label: '관리자 페이지', role: ['admin', 'root'] },
];

const LeftSidebar = ({ drawerWidth, topBarHeight = 64, mobileOpen, sidebarOpen, onDrawerToggle, onSidebarToggle, forceMobile = false }) => {
  const theme = useTheme();
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  
  // 모바일 뷰 강제 활성화 시
  const isMobileMode = forceMobile || isMobile || isMobileView;

  // 실제 사용자 역할 확인
  const { userRole } = useUserRole();
  const currentUserRole = userRole || 'user'; // user, manager, admin, root

  const isActive = (path, mobilePath) => {
    if (isMobileMode && mobilePath) {
      return location.pathname.startsWith(mobilePath);
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path, mobilePath, disabled) => {
    if (disabled) return;
    // 모바일 모드일 때는 모바일 경로로 이동
    const targetPath = (isMobileMode && mobilePath) ? mobilePath : path;
    navigate(targetPath);
    if (isMobileMode) {
      onDrawerToggle();
    }
  };

  // 역할에 따른 메뉴 필터링
  // TODO: 실제 인증 시스템 연동 후 활성화
  const filteredMenu = PRIMARY_MENU.filter(item => {
    if (!item.role) return true;
    // 모바일 모드에서는 관리자 페이지 메뉴 제외 (PC 버전에서만 접근 가능)
    if (isMobileMode && item.path === '/admin') {
      return false;
    }
    return item.role.includes(currentUserRole);
  });

  const renderMenuItems = (items) => (
    <List disablePadding sx={{ py: 0.5 }}>
      {items.map(({ icon: Icon, path, mobilePath, label, disabled }) => (
        <Tooltip 
          key={path}
          title={!sidebarOpen ? label : ''} 
          placement="right"
          arrow
        >
          <ListItemButton
            onClick={() => handleNavigation(path, mobilePath, disabled)}
            selected={isActive(path, mobilePath)}
            disabled={disabled}
            sx={{
              minHeight: 48,
              px: sidebarOpen ? 2.5 : 1.5,
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              transition: `all ${DOOR_DURATION}ms ${DOOR_TRANSITION}`,
              '&.Mui-selected': {
                backgroundColor: isDark 
                  ? 'rgba(138, 124, 255, 0.16)' 
                  : 'rgba(138, 124, 255, 0.08)',
                borderLeft: sidebarOpen ? `3px solid ${colors.accent}` : 'none',
                borderRight: !sidebarOpen && isActive(path) ? `3px solid ${colors.accent}` : 'none',
                '&:hover': {
                  backgroundColor: isDark 
                    ? 'rgba(138, 124, 255, 0.22)' 
                    : 'rgba(138, 124, 255, 0.12)',
                },
              },
              '&:hover': {
                backgroundColor: colors.hoverBg,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: sidebarOpen ? 40 : 0,
                justifyContent: 'center',
                color: isActive(path) ? colors.accent : colors.secondaryText,
              }}
            >
              <Icon size={20} />
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText
                primary={label}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: isActive(path) ? 600 : 400,
                  color: isActive(path) ? colors.primaryText : colors.secondaryText,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      ))}
    </List>
  );

  return (
    <Drawer
      variant={isMobileMode ? 'temporary' : 'permanent'}
      open={isMobileMode ? mobileOpen : (forceMobile ? false : sidebarOpen)}
      onClose={onDrawerToggle}
      sx={{
        width: sidebarOpen ? drawerWidth : COLLAPSED_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobileMode ? drawerWidth : (sidebarOpen ? drawerWidth : COLLAPSED_WIDTH),
          boxSizing: 'border-box',
          position: 'fixed', // 레이아웃 흐름에서 분리하여 중복 계산 방지
          left: 0, // 화면 좌측에 고정
          backgroundColor: colors.secondaryBg,
          borderRight: `1px solid ${colors.primaryBorder}`,
          transition: `width ${DOOR_DURATION}ms ${DOOR_TRANSITION}`,
          overflowX: 'hidden',
          top: topBarHeight,
          height: `calc(100% - ${topBarHeight}px)`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* 메뉴 헤더 */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${colors.primaryBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {sidebarOpen && (
            <Typography
              variant="h6"
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: colors.secondaryText,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              메뉴
            </Typography>
          )}
          {onSidebarToggle && (
            <Tooltip title={sidebarOpen ? '사이드바 접기' : '사이드바 펼치기'} placement="right" arrow>
              <IconButton
                onClick={onSidebarToggle}
                sx={{
                  color: colors.secondaryText,
                  backgroundColor: 'transparent',
                  minWidth: 32,
                  minHeight: 32,
                  borderRadius: 1,
                  ml: sidebarOpen ? 1 : 0,
                  '&:hover': {
                    backgroundColor: colors.hoverBg,
                    color: colors.accent,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {sidebarOpen !== false ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* 메인 메뉴 */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
          {renderMenuItems(filteredMenu)}
        </Box>
      </Box>
    </Drawer>
  );
};

export default LeftSidebar;
